import { BreathingPatternDto } from '../../api';

/**
 * Pure model of the breathing pacer (MI-004).
 *
 * The pattern comes from the server timeline (MI-002); this module only turns it
 * into an ordered list of steps and answers "where is the breath at second N".
 * Position is always derived from elapsed time, so the caller can drive it from
 * the session clock and pause and resume land on the exact same place.
 */

export type BreathingStepKey = 'inhale' | 'hold' | 'exhale' | 'holdOut';

/** Circle size at the bottom and at the top of the breath. */
export const MIN_PACER_SCALE = 0.55;
export const MAX_PACER_SCALE = 1;

const STEP_ORDER: readonly BreathingStepKey[] = ['inhale', 'hold', 'exhale', 'holdOut'];

const STEP_SCALES: Record<BreathingStepKey, { from: number; to: number }> = {
  inhale: { from: MIN_PACER_SCALE, to: MAX_PACER_SCALE },
  hold: { from: MAX_PACER_SCALE, to: MAX_PACER_SCALE },
  exhale: { from: MAX_PACER_SCALE, to: MIN_PACER_SCALE },
  holdOut: { from: MIN_PACER_SCALE, to: MIN_PACER_SCALE },
};

export interface BreathingStep {
  key: BreathingStepKey;
  durationSeconds: number;
  fromScale: number;
  toScale: number;
}

export interface BreathingPlan {
  steps: readonly BreathingStep[];
  cycleSeconds: number;
  cycles: number;
  totalSeconds: number;
}

export interface BreathingPacerPosition {
  /** One based, clamped to the number of cycles in the plan. */
  cycle: number;
  cycles: number;
  step: BreathingStep;
  stepElapsedSeconds: number;
  stepRemainingSeconds: number;
  stepProgress: number;
  scale: number;
  isComplete: boolean;
}

const positiveSeconds = (value?: number | null): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

const positiveCycles = (value?: number | null): number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 1 ? Math.floor(value) : 0;

const stepDurations = (pattern: BreathingPatternDto): Record<BreathingStepKey, number> => ({
  inhale: positiveSeconds(pattern.inhaleSeconds),
  hold: positiveSeconds(pattern.holdSeconds),
  exhale: positiveSeconds(pattern.exhaleSeconds),
  holdOut: positiveSeconds(pattern.holdOutSeconds),
});

/**
 * Builds the playable plan of a breathing pattern, or null when the pattern
 * carries no usable rhythm. Steps with no duration are dropped, so a 4-2-6-0
 * pattern plays as inhale, hold, exhale.
 */
export const buildBreathingPlan = (
  pattern?: BreathingPatternDto | null,
): BreathingPlan | null => {
  if (!pattern) {
    return null;
  }

  const cycles = positiveCycles(pattern.cycles);

  if (cycles === 0) {
    return null;
  }

  const durations = stepDurations(pattern);
  const steps: BreathingStep[] = STEP_ORDER.filter((key) => durations[key] > 0).map((key) => ({
    key,
    durationSeconds: durations[key],
    fromScale: STEP_SCALES[key].from,
    toScale: STEP_SCALES[key].to,
  }));

  if (steps.length === 0) {
    return null;
  }

  const cycleSeconds = steps.reduce((total, step) => total + step.durationSeconds, 0);

  return {
    steps,
    cycleSeconds,
    cycles,
    totalSeconds: cycleSeconds * cycles,
  };
};

/**
 * Length of the breathing phase implied by the pattern, or null when the pattern
 * has no usable rhythm. This is what makes the phase end exactly at the end of
 * the last cycle instead of a second before or after it.
 */
export const breathingPatternDurationSeconds = (
  pattern?: BreathingPatternDto | null,
): number | null => buildBreathingPlan(pattern)?.totalSeconds ?? null;

const interpolateScale = (step: BreathingStep, progress: number): number =>
  step.fromScale + (step.toScale - step.fromScale) * progress;

/** Where the breath is after the given number of seconds inside the phase. */
export const breathingPositionAt = (
  plan: BreathingPlan,
  elapsedSeconds: number,
): BreathingPacerPosition => {
  const lastStep = plan.steps[plan.steps.length - 1];
  const elapsed = Math.min(Math.max(elapsedSeconds, 0), plan.totalSeconds);

  if (elapsed >= plan.totalSeconds) {
    return {
      cycle: plan.cycles,
      cycles: plan.cycles,
      step: lastStep,
      stepElapsedSeconds: lastStep.durationSeconds,
      stepRemainingSeconds: 0,
      stepProgress: 1,
      scale: lastStep.toScale,
      isComplete: true,
    };
  }

  const cycleIndex = Math.floor(elapsed / plan.cycleSeconds);
  let withinCycle = elapsed - cycleIndex * plan.cycleSeconds;
  let step = plan.steps[0];

  for (let index = 0; index < plan.steps.length; index += 1) {
    step = plan.steps[index];

    if (withinCycle < step.durationSeconds || index === plan.steps.length - 1) {
      break;
    }

    withinCycle -= step.durationSeconds;
  }

  const stepElapsedSeconds = Math.min(withinCycle, step.durationSeconds);
  const stepProgress = step.durationSeconds === 0 ? 1 : stepElapsedSeconds / step.durationSeconds;

  return {
    cycle: cycleIndex + 1,
    cycles: plan.cycles,
    step,
    stepElapsedSeconds,
    stepRemainingSeconds: Math.max(0, step.durationSeconds - stepElapsedSeconds),
    stepProgress,
    scale: interpolateScale(step, stepProgress),
    isComplete: false,
  };
};
